import { FormattedMessage, useIntl } from 'react-intl';

import FallbackContent from '~/components/Shared/FallbackContent/FallbackContent';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { QueryParamType } from '~/enums/QueryParamType';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import Icon from '~/ui/components/Icon/Icon';
import Link from '~/ui/components/Link/Link';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

function Page() {
  const intl = useIntl();
  const exportId = getPageSearchQueryByKey(QueryParamType.ExportId);
  const { data, error, isFetching } =
    api.endpoints.checkDownloadExportReportById.useQuery(exportId);
  const status = coerceRtkqError(error)?.response?.status;
  const expiredExportId = status === HttpStatusCodeType.Gone;
  const notFoundExportId = status === HttpStatusCodeType.NotFound;

  function generateContent(icon: string, iconColor: string, content: any) {
    return (
      <FallbackContent
        icon={<Icon color={iconColor} icon={icon} size="xl" />}
        title={content}
      />
    );
  }

  if (isFetching) {
    return generateContent(
      'loading',
      'black-alpha-75',
      intl.formatMessage({
        id: 'DownloadExportReport.verifyingMessage',
        defaultMessage: 'Your download link is being verified.',
        description: 'Message shown while verifying download link',
      }),
    );
  } else if (expiredExportId) {
    return generateContent(
      'reject',
      'red-600',
      intl.formatMessage({
        id: 'DownloadExportReport.expiredMessage',
        defaultMessage: 'Your download link expired.',
        description: 'Error message when download link has expired',
      }),
    );
  } else if (notFoundExportId) {
    return generateContent(
      'reject',
      'red-600',
      intl.formatMessage({
        id: 'DownloadExportReport.notFoundMessage',
        defaultMessage: 'Your download link does not exist.',
        description: 'Error message when download link does not exist',
      }),
    );
  } else {
    const url = data?.url;
    if (url) {
      const btn = (
        <FormattedMessage
          id="DownloadExportReport.downloadButton"
          defaultMessage="<link>Click here to download report</link>"
          description="Button text for downloading report"
          values={{
            link: (chunks) => (
              <Link variant="button" href={url}>
                {chunks}
              </Link>
            ),
          }}
        />
      );
      return generateContent('approve', 'green-500', btn);
    } else {
      return generateContent(
        'reject',
        'red-600',
        intl.formatMessage({
          id: 'DownloadExportReport.linkNotFoundMessage',
          defaultMessage:
            'Your download link is not found. Please re-run the export report.',
          description: 'Error message when download link is not found',
        }),
      );
    }
  }
}

export default Page;
